import { useCallback, useEffect, useState } from "react";
import { useLang } from "@/i18n/LangContext";
import api, { formatApiError } from "@/lib/api";

export default function Proposals() {
  const { t, lang } = useLang();
  const [proposals, setProposals] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/proposals");
      setProposals(data.proposals);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (id, decision) => {
    setBusyId(id);
    setError("");
    try {
      await api.post(`/proposals/${id}/decide`, { decision });
      setProposals((ps) => ps.filter((p) => p.id !== id));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusyId(null);
    }
  };

  const kindLabel = (k) => {
    if (k === "event") return t("app.kindEvent");
    if (k === "task") return t("app.kindTask");
    return t("app.kindShopping");
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString(lang === "pt" ? "pt-PT" : "en-GB", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="px-6 md:px-12 lg:px-16 py-12 md:py-16 max-w-5xl">
      <p className="label-cap">— {t("app.sidebar2.proposals")}</p>
      <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight mt-3" data-testid="proposals-title">
        {t("app.proposalsTitle")}
      </h1>
      <p className="font-display-italic text-[var(--ink-soft)] mt-4 max-w-3xl text-lg leading-relaxed">
        {t("app.proposalsSub")}
      </p>

      {error && <p className="text-[var(--error)] text-sm mt-6">{error}</p>}

      <div className="mt-10 space-y-px bg-[var(--border)]" data-testid="proposals-list">
        {loading && <div className="bg-[var(--bg)] p-6 animate-pulse h-20" />}
        {!loading && proposals.length === 0 && (
          <p className="bg-[var(--bg)] py-12 text-center font-display-italic text-[var(--ink-soft)]" data-testid="proposals-empty">
            {t("app.proposalsEmpty")}
          </p>
        )}
        {proposals.map((p) => {
          const pl = p.payload || {};
          return (
            <article key={p.id} className="bg-[var(--bg)] p-6 grid grid-cols-12 gap-4 items-start" data-testid={`proposal-${p.id}`}>
              <div className="col-span-12 md:col-span-2">
                <p className="label-cap text-[var(--oxblood)]">{kindLabel(p.kind)}</p>
                <p className="text-xs text-[var(--ink-faint)] mt-1">
                  {t("app.proposalsSource")}: {p.source}
                </p>
              </div>
              <div className="col-span-12 md:col-span-7">
                <p className="font-display text-xl leading-snug">{pl.title || "(sem título)"}</p>
                {p.kind === "event" && pl.start && (
                  <p className="text-sm text-[var(--ink-soft)] mt-2">
                    {fmtDate(pl.start)}
                    {pl.end ? ` → ${fmtDate(pl.end)}` : ""}
                    {pl.location ? ` · ${pl.location}` : ""}
                  </p>
                )}
                {pl.notes && (
                  <p className="text-sm text-[var(--ink-soft)] mt-2 leading-relaxed">{pl.notes}</p>
                )}
                {pl.qty && <p className="text-sm text-[var(--ink-soft)] mt-2">qty: {pl.qty}</p>}
                {p.raw_excerpt && (
                  <p className="text-xs text-[var(--ink-faint)] mt-3 italic">
                    «{p.raw_excerpt.slice(0, 140)}{p.raw_excerpt.length > 140 ? "…" : ""}»
                  </p>
                )}
              </div>
              <div className="col-span-12 md:col-span-3 flex md:justify-end gap-3 flex-wrap">
                <button
                  onClick={() => decide(p.id, "decline")}
                  disabled={busyId === p.id}
                  className="btn-ghost"
                  data-testid={`proposal-decline-${p.id}`}
                >
                  {t("app.proposalsDecline")}
                </button>
                <button
                  onClick={() => decide(p.id, "approve")}
                  disabled={busyId === p.id}
                  className="btn-primary"
                  data-testid={`proposal-approve-${p.id}`}
                >
                  {t("app.proposalsApprove")}
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
